import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import logo from '../assets/ChatGPT Image Aug 10, 2026, 06_50_42 PM.png';
import CTASection from './CTASection';
import TalkToExpert from './TalkToExpert';

const domains = [
  {
    id: 'ai-ml',
    icon: '🤖',
    gradient: 'from-amber-400 to-orange-500',
    bg: 'from-amber-50 to-orange-50',
    border: 'border-amber-200',
    badge: 'Most Popular',
    badgeColor: 'bg-amber-100 text-amber-700',
    title: 'Artificial Intelligence & Machine Learning',
    tagline: 'Build intelligent systems that think, learn & adapt',
    description: 'Our AI/ML team comprises PhD holders and industry veterans from top IITs who build state-of-the-art models trained on real datasets with proven SOTA benchmark results.',
    deliverables: [
      'Complete Python source code with comments',
      'Trained model files (.h5, .pkl, .pt)',
      'Dataset collection & preprocessing scripts',
      'Research paper (IEEE / Springer format)',
      'Result graphs, confusion matrix, ROC curves',
      'PPT presentation (20–30 slides)',
      'Demo video with live inference',
      'Viva Q&A guide (100+ expected questions)',
    ],
    technologies: ['TensorFlow', 'PyTorch', 'Keras', 'Scikit-learn', 'OpenCV', 'Hugging Face', 'YOLO', 'BERT/GPT'],
    projectExamples: [
      { title: 'Brain Tumour Detection using CNN', level: 'BTech / MTech', days: '5–7 days' },
      { title: 'Real-time Object Detection (YOLO v8)', level: 'MTech', days: '7–10 days' },
      { title: 'Fake News Detection using NLP', level: 'BTech', days: '4–6 days' },
      { title: 'Emotion Recognition from Speech', level: 'MTech / PhD', days: '10–14 days' },
    ],
    why: 'We achieve >95% accuracy consistently. Every model is trained, validated, and tested — not just copy-pasted.',
    timeline: '3–14 days',
    price: '₹5,999',
  },
  {
    id: 'iot',
    icon: '📡',
    gradient: 'from-green-400 to-teal-500',
    bg: 'from-green-50 to-teal-50',
    border: 'border-green-200',
    badge: 'Hardware + Software',
    badgeColor: 'bg-green-100 text-green-700',
    title: 'IoT & Embedded Systems',
    tagline: 'From sensors to smart systems — end-to-end IoT solutions',
    description: 'Our embedded engineers design, simulate and implement complete IoT projects with real circuit diagrams, working code, and cloud dashboards for monitoring.',
    deliverables: [
      'Circuit diagram (Fritzing / Proteus simulation)',
      'Microcontroller code (C / MicroPython)',
      'Cloud dashboard (Blynk, ThingSpeak, Firebase)',
      'Component list with Pinout guide',
      'Complete project report with block diagram',
      'Hardware demonstration video',
      'PPT presentation with circuit explanation',
      'Troubleshooting guide',
    ],
    technologies: ['Arduino', 'ESP32', 'ESP8266', 'Raspberry Pi', 'NodeMCU', 'STM32', 'MQTT', 'LoRa'],
    projectExamples: [
      { title: 'Smart Agriculture using IoT + ML', level: 'BTech ECE/CSE', days: '5–8 days' },
      { title: 'Health Monitoring Wearable System', level: 'BTech BME/ECE', days: '6–9 days' },
      { title: 'Smart Home Automation with Voice', level: 'Diploma / BTech', days: '3–5 days' },
      { title: 'Industrial IoT Predictive Maintenance', level: 'MTech', days: '10–15 days' },
    ],
    why: 'Real Proteus simulations + code tested on actual hardware. We provide component lists for easy purchase.',
    timeline: '3–15 days',
    price: '₹4,999',
  },
  {
    id: 'data-science',
    icon: '📊',
    gradient: 'from-purple-400 to-violet-600',
    bg: 'from-purple-50 to-violet-50',
    border: 'border-purple-200',
    badge: 'Industry Ready',
    badgeColor: 'bg-purple-100 text-purple-700',
    title: 'Data Science & Analytics',
    tagline: 'Turn raw data into powerful insights and decisions',
    description: 'From exploratory data analysis to advanced predictive models and interactive dashboards — our data scientists deliver industry-grade analytics projects that impress evaluators.',
    deliverables: [
      'Cleaned & preprocessed dataset',
      'EDA notebooks with visualizations (Matplotlib, Seaborn, Plotly)',
      'Machine learning pipeline with evaluation metrics',
      'Interactive dashboard (Tableau / Power BI / Streamlit)',
      'Business insights report',
      'Full Jupyter notebook with markdown explanations',
      'PPT & project report',
      'Live deployment on Streamlit Cloud / Heroku',
    ],
    technologies: ['Python', 'Pandas', 'NumPy', 'Matplotlib', 'Seaborn', 'Plotly', 'Tableau', 'Power BI', 'Streamlit', 'Apache Spark'],
    projectExamples: [
      { title: 'Customer Churn Prediction Dashboard', level: 'MBA / MCA / BTech', days: '5–7 days' },
      { title: 'Stock Market Forecasting (LSTM)', level: 'BTech / MTech', days: '6–9 days' },
      { title: 'COVID-19 Data Analysis & Visualization', level: 'BSc / BTech', days: '3–5 days' },
      { title: 'Big Data Sales Analytics (Spark)', level: 'MTech / MCA', days: '8–12 days' },
    ],
    why: 'Real Kaggle datasets + deployed live links. Your evaluators will see a working app, not just screenshots.',
    timeline: '3–12 days',
    price: '₹4,999',
  },
  {
    id: 'cloud-devops',
    icon: '☁️',
    gradient: 'from-sky-400 to-blue-600',
    bg: 'from-sky-50 to-blue-50',
    border: 'border-sky-200',
    badge: 'Enterprise Grade',
    badgeColor: 'bg-sky-100 text-sky-700',
    title: 'Cloud Computing & DevOps',
    tagline: 'Deploy, scale and automate — modern cloud architecture',
    description: 'Our certified AWS/Azure engineers architect real cloud infrastructure projects with CI/CD pipelines, containerized deployments, and cost-optimized solutions.',
    deliverables: [
      'Architecture diagram (AWS / Azure / GCP)',
      'Infrastructure-as-Code (Terraform / CloudFormation)',
      'Docker containers + Kubernetes manifests',
      'CI/CD pipeline configuration (GitHub Actions / Jenkins)',
      'Monitoring setup (Prometheus + Grafana)',
      'Security configuration & best practices doc',
      'Cost analysis & optimization report',
      'Step-by-step deployment guide',
    ],
    technologies: ['AWS', 'Azure', 'GCP', 'Docker', 'Kubernetes', 'Terraform', 'Jenkins', 'GitHub Actions', 'Prometheus', 'Ansible'],
    projectExamples: [
      { title: 'Serverless Microservices on AWS Lambda', level: 'MTech / MCA', days: '7–10 days' },
      { title: 'Kubernetes Cluster with Auto-scaling', level: 'MTech / BE', days: '8–12 days' },
      { title: 'CI/CD Pipeline for Web Application', level: 'BTech / MCA', days: '5–7 days' },
      { title: 'Multi-Cloud Disaster Recovery System', level: 'MTech / MBA IT', days: '10–14 days' },
    ],
    why: 'Real AWS accounts used for demo. Architecture designs follow industry best practices used at Fortune 500 companies.',
    timeline: '5–14 days',
    price: '₹6,999',
  },
  {
    id: 'web-mobile',
    icon: '💻',
    gradient: 'from-pink-400 to-rose-500',
    bg: 'from-pink-50 to-rose-50',
    border: 'border-pink-200',
    badge: 'Full Stack',
    badgeColor: 'bg-pink-100 text-pink-700',
    title: 'Web & Mobile Development',
    tagline: 'Beautiful, functional apps for web and mobile platforms',
    description: 'Our full-stack developers build complete, deployable web and mobile applications with modern UI/UX, authentication, databases, and live hosting.',
    deliverables: [
      'Full source code (Frontend + Backend)',
      'Database schema & seed data',
      'User authentication & role management',
      'REST API documentation (Postman collection)',
      'Responsive UI design (mobile + desktop)',
      'Admin dashboard / control panel',
      'Deployed live link (Vercel / Netlify / Firebase)',
      'Complete project report & UML diagrams',
    ],
    technologies: ['React.js', 'Node.js', 'Express', 'MongoDB', 'Angular', 'Vue.js', 'Flutter', 'React Native', 'Next.js', 'PostgreSQL'],
    projectExamples: [
      { title: 'E-Learning Platform with AI Recommendations', level: 'MCA / BTech', days: '10–15 days' },
      { title: 'Hospital Management System (MERN)', level: 'MCA / BTech', days: '8–12 days' },
      { title: 'Food Delivery App (Flutter)', level: 'BTech / MCA', days: '7–10 days' },
      { title: 'Real-time Chat App with WebSockets', level: 'BTech / MCA', days: '5–8 days' },
    ],
    why: 'Live deployment included. Your project will have a real URL your evaluators can visit — not just localhost screenshots.',
    timeline: '5–15 days',
    price: '₹5,999',
  },
  {
    id: 'blockchain',
    icon: '⛓️',
    gradient: 'from-yellow-400 to-amber-600',
    bg: 'from-yellow-50 to-amber-50',
    border: 'border-yellow-200',
    badge: 'Web3 Ready',
    badgeColor: 'bg-yellow-100 text-yellow-700',
    title: 'Blockchain & Web3',
    tagline: 'Smart contracts, DeFi and decentralized applications',
    description: 'Our blockchain experts deploy real smart contracts on testnets, build DApps with MetaMask integration, and create NFT platforms with complete transaction flows.',
    deliverables: [
      'Solidity smart contract with full testing suite',
      'Contract deployed on testnet (Sepolia / Mumbai)',
      'Frontend DApp with Web3.js / Ethers.js',
      'MetaMask wallet integration',
      'Smart contract audit report',
      'Transaction flow documentation',
      'Gas optimization analysis',
      'Complete project report & whitepaper draft',
    ],
    technologies: ['Solidity', 'Ethereum', 'Polygon', 'Hardhat', 'Truffle', 'Web3.js', 'Ethers.js', 'IPFS', 'OpenZeppelin', 'Chainlink'],
    projectExamples: [
      { title: 'Decentralized Voting System', level: 'MTech / MCA / BTech', days: '7–10 days' },
      { title: 'NFT Marketplace with Minting', level: 'BTech / MTech', days: '10–14 days' },
      { title: 'DeFi Lending Protocol', level: 'MTech / PhD', days: '12–18 days' },
      { title: 'Supply Chain Transparency on Blockchain', level: 'MBA / MTech', days: '8–12 days' },
    ],
    why: 'Real testnet deployments with etherscan links. Evaluators can verify your contract on the blockchain.',
    timeline: '7–18 days',
    price: '₹7,999',
  },
  {
    id: 'cybersecurity',
    icon: '🔐',
    gradient: 'from-red-400 to-rose-600',
    bg: 'from-red-50 to-rose-50',
    border: 'border-red-200',
    badge: 'Ethical Hacking',
    badgeColor: 'bg-red-100 text-red-700',
    title: 'Cybersecurity & Network Security',
    tagline: 'Protect, detect and respond — advanced security projects',
    description: 'Our CEH-certified security experts design complete cybersecurity projects covering penetration testing frameworks, intrusion detection systems, and cryptographic implementations.',
    deliverables: [
      'Security tool / framework source code',
      'Penetration testing report (if applicable)',
      'Threat model & risk assessment document',
      'Network topology diagram',
      'Lab setup guide (VMs / GNS3)',
      'Security algorithms implementation',
      'Vulnerability analysis report',
      'Project report with future scope',
    ],
    technologies: ['Python', 'Metasploit', 'Wireshark', 'Nmap', 'Snort', 'Kali Linux', 'OpenSSL', 'GNS3', 'Burp Suite', 'SIEM'],
    projectExamples: [
      { title: 'AI-based Intrusion Detection System', level: 'MTech / BTech', days: '7–10 days' },
      { title: 'Network Packet Analyzer & Visualizer', level: 'BTech / MCA', days: '5–7 days' },
      { title: 'Ransomware Detection using ML', level: 'MTech / PhD', days: '8–12 days' },
      { title: 'Zero-Trust Security Architecture', level: 'MTech / MBA IT', days: '10–15 days' },
    ],
    why: 'All projects done in controlled lab environments. We follow ethical guidelines and provide detailed methodology.',
    timeline: '5–15 days',
    price: '₹6,999',
  },
  {
    id: 'research',
    icon: '📚',
    gradient: 'from-indigo-400 to-blue-700',
    bg: 'from-indigo-50 to-blue-50',
    border: 'border-indigo-200',
    badge: 'Publication Ready',
    badgeColor: 'bg-indigo-100 text-indigo-700',
    title: 'Research Papers & PhD Support',
    tagline: 'From idea to publication — complete research journey',
    description: 'Our academic team of 50+ researchers and PhD holders from IITs and NITs craft publication-quality research papers for SCI, Scopus, and IEEE journals with 0% plagiarism guarantee.',
    deliverables: [
      'Full research paper (6–12 pages, IEEE / Springer)',
      'Literature review (50–100 relevant papers)',
      'Proposed algorithm / methodology',
      'Experimental results with comparison tables',
      'Plagiarism report (Turnitin < 10%)',
      'Journal/conference submission ready format',
      'Response to reviewer comments (if needed)',
      'LaTeX / Word format as required',
    ],
    technologies: ['LaTeX', 'MATLAB', 'Python', 'R', 'SPSS', 'Mendeley', 'Zotero', 'Overleaf', 'IEEE Template', 'Springer Template'],
    projectExamples: [
      { title: 'Survey on Federated Learning for Healthcare', level: 'PhD / MTech', days: '10–15 days' },
      { title: 'Novel CNN Architecture for Medical Imaging', level: 'PhD / MTech', days: '14–20 days' },
      { title: 'Blockchain for Secure Data Sharing (IEEE)', level: 'MTech / PhD', days: '10–14 days' },
      { title: 'Comparative Study on NLP Transformers', level: 'MTech / PhD', days: '12–18 days' },
    ],
    why: 'We have successfully published in 200+ journals. Our researchers know exactly what reviewers look for.',
    timeline: '10–20 days',
    price: '₹8,999',
  },
];

function DomainCard({ domain, index, onClick, isSelected }) {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: (index % 4) * 0.1, duration: 0.5 }}
      onClick={onClick}
      className={`cursor-pointer rounded-2xl p-6 border-2 transition-all duration-300 hover:-translate-y-1 group relative overflow-hidden ${
        isSelected
          ? 'border-royal-gold shadow-gold bg-royal-gold-pale'
          : `bg-gradient-to-br ${domain.bg} ${domain.border} hover:border-royal-gold/60 hover:shadow-gold`
      }`}
    >
      {/* Shimmer on selected */}
      {isSelected && (
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-royal-gold to-transparent" />
      )}

      <div className="flex items-start gap-4">
        <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${domain.gradient} flex items-center justify-center text-2xl shadow-md flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
          {domain.icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="font-playfair font-bold text-royal-navy text-base leading-tight">{domain.title}</h3>
          </div>
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${domain.badgeColor}`}>
            {domain.badge}
          </span>
          <p className="font-inter text-xs text-slate-royal/70 mt-2 leading-relaxed line-clamp-2">{domain.tagline}</p>
          <div className="flex items-center gap-3 mt-3">
            <span className="font-inter text-xs text-royal-gold font-bold">{domain.price}+</span>
            <span className="text-slate-royal/30">•</span>
            <span className="font-inter text-xs text-slate-royal/60">{domain.timeline}</span>
          </div>
        </div>
        <div className={`w-7 h-7 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-1 transition-all duration-300 ${isSelected ? 'bg-royal-gold border-royal-gold' : 'border-parchment-dark group-hover:border-royal-gold'}`}>
          <svg className={`w-3.5 h-3.5 ${isSelected ? 'text-white' : 'text-slate-royal/40 group-hover:text-royal-gold'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </motion.div>
  );
}

function DomainDetail({ domain }) {
  return (
    <motion.div
      key={domain.id}
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -30 }}
      transition={{ duration: 0.4 }}
      className="space-y-8"
    >
      {/* Header */}
      <div className={`rounded-3xl p-8 bg-gradient-to-br ${domain.bg} border ${domain.border} relative overflow-hidden`}>
        <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-transparent via-royal-gold to-transparent" />
        <div className="flex items-start gap-5">
          <div className={`w-20 h-20 rounded-3xl bg-gradient-to-br ${domain.gradient} flex items-center justify-center text-4xl shadow-lg flex-shrink-0`}>
            {domain.icon}
          </div>
          <div>
            <span className={`text-xs font-bold px-3 py-1 rounded-full ${domain.badgeColor} mb-3 inline-block`}>
              {domain.badge}
            </span>
            <h2 className="font-playfair text-2xl md:text-3xl font-bold text-royal-navy mb-2">{domain.title}</h2>
            <p className="font-inter text-royal-gold font-semibold text-sm italic">"{domain.tagline}"</p>
          </div>
        </div>
        <p className="font-inter text-slate-royal/80 text-sm leading-relaxed mt-5 border-t border-black/5 pt-5">
          {domain.description}
        </p>
        <div className="flex gap-6 mt-4">
          <div>
            <p className="font-inter text-xs text-slate-royal/50 uppercase tracking-wider">Starting From</p>
            <p className="font-playfair text-2xl font-bold text-royal-gold">{domain.price}</p>
          </div>
          <div className="w-px bg-black/10" />
          <div>
            <p className="font-inter text-xs text-slate-royal/50 uppercase tracking-wider">Delivery</p>
            <p className="font-playfair text-2xl font-bold text-royal-navy">{domain.timeline}</p>
          </div>
          <div className="w-px bg-black/10" />
          <div>
            <p className="font-inter text-xs text-slate-royal/50 uppercase tracking-wider">Revisions</p>
            <p className="font-playfair text-2xl font-bold text-green-600">Unlimited</p>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Deliverables */}
        <div className="royal-card p-6">
          <h3 className="font-playfair font-bold text-royal-navy text-lg mb-4 flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-royal-gold/15 flex items-center justify-center">
              <svg className="w-4 h-4 text-royal-gold" viewBox="0 0 24 24" fill="currentColor">
                <path fillRule="evenodd" d="M9 1.5H5.625c-1.036 0-1.875.84-1.875 1.875v17.25c0 1.035.84 1.875 1.875 1.875h12.75c1.035 0 1.875-.84 1.875-1.875V12.75A3.75 3.75 0 0016.5 9h-1.875a1.875 1.875 0 01-1.875-1.875V5.25A3.75 3.75 0 009 1.5zm6.61 10.936a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 14.47a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clipRule="evenodd"/>
              </svg>
            </span>
            What You Get
          </h3>
          <ul className="space-y-2.5">
            {domain.deliverables.map((item, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <div className="w-4 h-4 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-2.5 h-2.5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                  </svg>
                </div>
                <span className="font-inter text-sm text-slate-royal/80">{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Technologies */}
        <div className="space-y-6">
          <div className="royal-card p-6">
            <h3 className="font-playfair font-bold text-royal-navy text-lg mb-4 flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-royal-navy/10 flex items-center justify-center">
                <svg className="w-4 h-4 text-royal-navy" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5"/>
                </svg>
              </span>
              Technologies Used
            </h3>
            <div className="flex flex-wrap gap-2">
              {domain.technologies.map((tech) => (
                <span key={tech} className="font-inter text-xs font-semibold bg-royal-navy/5 border border-royal-navy/15 text-royal-navy px-3 py-1.5 rounded-full hover:bg-royal-gold/10 hover:border-royal-gold/30 transition-colors cursor-default">
                  {tech}
                </span>
              ))}
            </div>
          </div>

          {/* Why Us */}
          <div className="royal-card p-6 border-l-4 border-royal-gold">
            <h3 className="font-playfair font-bold text-royal-navy text-base mb-2">Why Choose Us?</h3>
            <p className="font-inter text-sm text-slate-royal/75 leading-relaxed italic">"{domain.why}"</p>
          </div>
        </div>
      </div>

      {/* Sample Projects */}
      <div className="royal-card p-6">
        <h3 className="font-playfair font-bold text-royal-navy text-lg mb-5 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-royal-gold/15 flex items-center justify-center">
            <svg className="w-4 h-4 text-royal-gold" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z"/>
            </svg>
          </span>
          Sample Project Titles
        </h3>
        <div className="grid sm:grid-cols-2 gap-3">
          {domain.projectExamples.map((proj, i) => (
            <div key={i} className={`rounded-xl p-4 bg-gradient-to-br ${domain.bg} border ${domain.border} hover:shadow-gold hover:border-royal-gold/40 transition-all duration-300 group`}>
              <p className="font-inter font-semibold text-royal-navy text-sm group-hover:text-royal-gold transition-colors">{proj.title}</p>
              <div className="flex items-center gap-2 mt-2">
                <span className="font-inter text-xs text-slate-royal/60 bg-white/70 px-2 py-0.5 rounded-full">{proj.level}</span>
                <span className="font-inter text-xs text-royal-gold font-semibold">{proj.days}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div
        className="rounded-3xl p-8 text-center relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #0B1C4E 0%, #1E3A6E 100%)' }}
      >
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-royal-gold to-transparent" />
        <h3 className="font-playfair text-2xl font-bold text-white mb-2">Ready to Start Your {domain.title.split(' ')[0]} Project?</h3>
        <p className="font-inter text-white/70 text-sm mb-6">Get a free consultation from our domain expert within 1 hour</p>
        <div className="flex flex-wrap justify-center gap-4">
          <a
            href="#contact"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="font-inter font-bold px-8 py-3.5 rounded-full text-royal-navy text-sm transition-all hover:scale-105"
            style={{ background: 'linear-gradient(135deg, #C9A84C, #F0D080)', boxShadow: '0 4px 20px rgba(201,168,76,0.4)' }}
          >
            Get Free Consultation
          </a>
          <a
            href="https://wa.me/917676654809"
            target="_blank"
            rel="noopener noreferrer"
            className="border-2 border-white/30 text-white font-inter font-semibold px-8 py-3.5 rounded-full hover:bg-white/10 transition-all duration-300 text-sm inline-flex items-center gap-2"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            WhatsApp Now
          </a>
        </div>
      </div>
    </motion.div>
  );
}

export default function DomainsPage() {
  const [selected, setSelected] = useState(domains[0]);
  const [isExpertOpen, setIsExpertOpen] = useState(false);

  return (
    <>
      <div className="min-h-screen bg-ivory font-inter">
      {/* Page Header */}
      <div
        className="relative pt-24 pb-16 text-center overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #0B1C4E 0%, #122266 50%, #0B1C4E 100%)' }}
      >
        {/* Back to Home */}
        <a
          href="/"
          className="absolute top-6 left-6 flex items-center gap-2 text-white/60 hover:text-royal-gold transition-colors font-inter text-sm font-medium"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Home
        </a>

        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-6">
          <img src={logo} alt="Connect Students" className="h-12 w-12 object-contain" />
          <div className="text-left">
            <p className="font-playfair font-bold text-white text-lg">Connect Students</p>
            <p className="font-inter text-royal-gold text-xs tracking-widest">LEARN • CONNECT • GROW</p>
          </div>
        </div>

        <div className="inline-flex items-center gap-2 bg-white/10 border border-royal-gold/30 text-royal-gold-light text-sm font-semibold px-4 py-2 rounded-full mb-5">
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z"/>
          </svg>
          8 Premium Technology Domains
        </div>

        <h1 className="font-playfair text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 px-4">
          Explore Our{' '}
          <span style={{ background: 'linear-gradient(90deg, #C9A84C, #F0D080, #C9A84C)', backgroundSize: '200% auto', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', animation: 'shimmer 3s linear infinite' }}>
            Expert Domains
          </span>
        </h1>
        <p className="font-inter text-white/70 text-lg max-w-2xl mx-auto px-4">
          Comprehensive academic support across every cutting-edge technology. 
          Click any domain to explore full details, deliverables and sample projects.
        </p>

        {/* Stats bar */}
        <div className="flex flex-wrap justify-center gap-8 mt-10">
          {[
            { v: '8', l: 'Domains' },
            { v: '200+', l: 'Technologies' },
            { v: '5000+', l: 'Projects Delivered' },
            { v: '98%', l: 'Success Rate' },
          ].map((s) => (
            <div key={s.l} className="text-center">
              <p className="font-playfair text-2xl font-bold text-royal-gold">{s.v}</p>
              <p className="font-inter text-xs text-white/60">{s.l}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-5 gap-8">
          {/* LEFT: Domain list */}
          <div className="lg:col-span-2 space-y-3">
            <p className="font-inter text-xs text-slate-royal/50 font-semibold uppercase tracking-wider mb-4">
              Select a Domain
            </p>
            {domains.map((domain, i) => (
              <DomainCard
                key={domain.id}
                domain={domain}
                index={i}
                onClick={() => setSelected(domain)}
                isSelected={selected.id === domain.id}
              />
            ))}
          </div>

          {/* RIGHT: Domain detail */}
          <div className="lg:col-span-3">
            <div className="sticky top-24">
              <AnimatePresence mode="wait">
                <DomainDetail key={selected.id} domain={selected} />
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section instead of custom banner */}
      <CTASection onExpertClick={() => setIsExpertOpen(true)} />

    </div>

    {/* Global Modal */}
    <TalkToExpert 
      isOpen={isExpertOpen}
      onClose={() => setIsExpertOpen(false)}
    />
    </>
  );
}
